import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { nexusApi } from '../api/nexus';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';

const LiveStudio = () => {
  const navigate = useNavigate();
  const { isManagement, isAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [planData, setPlanData] = useState({
    products: [''],
    duration: '60min',
    platform: 'TikTok'
  });
  const [planId, setPlanId] = useState(null);
  const [script, setScript] = useState(null);
  const [report, setReport] = useState(null);

  const addProduct = () => {
    setPlanData({ ...planData, products: [...planData.products, ''] });
  };

  const updateProduct = (index, value) => {
    const newProducts = [...planData.products];
    newProducts[index] = value;
    setPlanData({ ...planData, products: newProducts });
  };

  const removeProduct = (index) => {
    const newProducts = planData.products.filter((_, i) => i !== index);
    setPlanData({ ...planData, products: newProducts });
  };

  const handleCreatePlan = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.livePlan(planData);
      setPlanId(response.data.plan_id);
      setStep(2);
    } catch (error) {
      console.error('Failed to create plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateScript = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.liveScriptGenerate({
        plan_id: planId,
        products: planData.products.filter(p => p.trim())
      });
      setScript(response.data);
      setStep(3);
    } catch (error) {
      console.error('Failed to generate script:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.liveReport({
        live_id: planId,
        duration: planData.duration,
        viewers: 'N/A'
      });
      setReport(response.data);
      setStep(4);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Platform</label>
        <select
          className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emzi-blue focus:ring-2 focus:ring-blue-100 outline-none transition-colors"
          value={planData.platform}
          onChange={(e) => setPlanData({ ...planData, platform: e.target.value })}
          data-testid="platform-select"
        >
          <option value="TikTok">TikTok Live</option>
          <option value="Facebook">Facebook Live</option>
          <option value="Instagram">Instagram Live</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Duration</label>
        <select
          className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emzi-blue focus:ring-2 focus:ring-blue-100 outline-none transition-colors"
          value={planData.duration}
          onChange={(e) => setPlanData({ ...planData, duration: e.target.value })}
          data-testid="duration-select"
        >
          <option value="30min">30 minutes</option>
          <option value="60min">60 minutes</option>
          <option value="90min">90 minutes</option>
          <option value="120min">120 minutes</option>
        </select>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium">Products to Feature</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addProduct}
            data-testid="add-product-button"
          >
            + Add Product
          </Button>
        </div>
        
        {planData.products.map((product, index) => (
          <div key={`item-${index}`} className="flex gap-2">
            <Input
              placeholder={`Product ${index + 1}`}
              value={product}
              onChange={(e) => updateProduct(index, e.target.value)}
              data-testid={`product-input-${index}`}
            />
            {planData.products.length > 1 && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeProduct(index)}
                data-testid={`remove-product-${index}`}
              >
                ×
              </Button>
            )}
          </div>
        ))}
      </div>

      <Button
        onClick={handleCreatePlan}
        disabled={loading || !planData.products.some(p => p.trim())}
        className="w-full bg-emzi-blue hover:bg-blue-600 text-white font-semibold h-12"
        data-testid="create-plan-button"
      >
        {loading ? 'Creating...' : 'Create Live Plan →'}
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription>
          📋 Plan Created: {planId}
        </AlertDescription>
      </Alert>

      <div className="bg-white rounded-lg p-4 border">
        <h3 className="font-semibold mb-2">Live Plan Summary</h3>
        <div className="text-sm space-y-1 text-gray-700">
          <p>Platform: {planData.platform}</p>
          <p>Duration: {planData.duration}</p>
          <p>Products: {planData.products.filter(p => p.trim()).length}</p>
        </div>
      </div>

      <Button
        onClick={handleGenerateScript}
        disabled={loading}
        className="w-full"
        data-testid="generate-script-button"
      >
        {loading ? 'Generating...' : 'Generate Live Script →'}
      </Button>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {script && (
        <>
          <Alert className="bg-green-50 border-green-200" data-testid="script-ready-alert">
            <AlertDescription>
              ✓ Live Script Generated (Teleprompter Ready)
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {script.segments?.map((segment, index) => (
              <Card key={segment.id} data-testid={`segment-${index}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base">{segment.product}</CardTitle>
                    <Badge>{segment.duration}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-600">Hook:</p>
                    <p className="text-sm">{segment.hook}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600">Script:</p>
                    <p className="text-sm whitespace-pre-wrap">{segment.script}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600">CTA:</p>
                    <p className="text-sm">{segment.cta}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {script.compliance_check && (
            <Alert className={script.compliance_check.status === 'ready_for_approval' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <AlertDescription>
                Compliance: {script.compliance_check.status === 'ready_for_approval' ? '✓ Pass' : '✗ Fail'} (Risk: {script.compliance_check.risk_score}/15)
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setStep(1); setScript(null); }}
              className="flex-1"
              data-testid="new-plan-button"
            >
              New Plan
            </Button>
            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="flex-1"
              data-testid="generate-report-button"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      {report && (
        <>
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription>
              📊 Post-Live Report Generated
            </AlertDescription>
          </Alert>

          <Card data-testid="report-card">
            <CardHeader>
              <CardTitle>Session Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Live ID</p>
                  <p className="font-semibold">{report.live_id}</p>
                </div>
                <div>
                  <p className="text-gray-600">Duration</p>
                  <p className="font-semibold">{report.duration}</p>
                </div>
                <div>
                  <p className="text-gray-600">Compliance</p>
                  <p className="font-semibold text-green-600">{report.compliance_violations} violations</p>
                </div>
                <div>
                  <p className="text-gray-600">BP Earned</p>
                  <p className="font-semibold text-blue-600">{report.bp_earned} BP</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setStep(1); setReport(null); setScript(null); }}
              className="flex-1"
              data-testid="new-session-button"
            >
              New Session
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="flex-1"
              data-testid="back-dashboard-button"
            >
              Back to Dashboard
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="nexus-content overflow-hidden bg-white">
      {/* EMZI Header - Normalized with Dashboard */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">🎥</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">Live Studio</h1>
              <p className="text-sm text-white font-medium">Multi-product live session planner</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {(isManagement() || isAdmin()) && (
              <span className="text-white text-xs font-medium opacity-90" title="System certified under EMZI ISO-Aligned Compliance Protocol">
                🟢 ISO Compliance Active
              </span>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105" data-testid="back-button">
              ← Back
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0">
        <Card className="shadow-xl border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}} data-testid="live-studio-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emzi-blue bg-opacity-10 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-emzi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-gray-900 font-bold">Live Studio</CardTitle>
                <CardDescription className="text-sm text-gray-600">Multi-product live session planner</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-emzi-blue' : 'bg-gray-200'
                  }`}
                  data-testid={`progress-step-${s}`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LiveStudio;
